import { useState, useContext, useEffect } from 'react';
import { Container, TextField, Button, Typography, Alert } from '@mui/material';
import { Link as RouterLink, useNavigate, useLocation } from 'react-router-dom';
import AuthContext from '../AuthContext';
import { apiFetch } from '../api';

export default function SignupPage() {
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const { login } = useContext(AuthContext);
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      const element = document.getElementById(location.hash.substring(1));
      if (element) {
        setTimeout(() => {
          const navbarHeight = 80; // Approximate navbar height
          const elementPosition = element.getBoundingClientRect().top + window.scrollY;
          window.scrollTo({
            top: elementPosition - navbarHeight,
            behavior: 'smooth',
          });
        }, 100);
      }
    } else {
      // Always scroll to top when there's no hash
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [location]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    try {
      const response = await apiFetch('/api/auth/register/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, email, password }),
      });

      if (!response.ok) {
        const errText = await response.text().catch(() => null);
        throw new Error(errText || 'Failed to register');
      }

      const data = await response.json();

      // Prefer user object returned by API, otherwise construct from inputs
      const user = data.user || { username: data.username || username, email: data.email || email };
      login(data.token, user);

      // Navigate to temporary signup redirect page
      navigate('/temp-signup');
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <div
      style={{
        backgroundColor: '#000000ff',
        minHeight: '100vh',
        paddingTop: '80px',
      }}
    >
      <Container sx={{ py: 4 }} maxWidth="sm">
        <Typography
          variant="h4"
          component="h1"
          gutterBottom
          style={{
            color: '#fff',
            fontFamily: 'Inter, sans-serif',
            fontSize: '3rem',
            fontWeight: 500,
            textAlign: 'center',
          }}
        >
          Sign Up
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <TextField
            label="Username"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            fullWidth
            margin="normal"
            required
            sx={{
              '& .MuiInputLabel-root': { color: '#fff' },
              '& .MuiOutlinedInput-root': {
                color: '#fff',
                '& fieldset': { borderColor: '#fff' },
                '&:hover fieldset': { borderColor: '#fff' },
                '&.Mui-focused fieldset': { borderColor: '#fff' },
              },
            }}
          />

          <TextField
            label="Email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            fullWidth
            margin="normal"
            required
            sx={{
              '& .MuiInputLabel-root': { color: '#fff' },
              '& .MuiOutlinedInput-root': {
                color: '#fff',
                '& fieldset': { borderColor: '#fff' },
                '&:hover fieldset': { borderColor: '#fff' },
                '&.Mui-focused fieldset': { borderColor: '#fff' },
              },
            }}
          />

          <TextField
            label="Password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            fullWidth
            margin="normal"
            required
            sx={{
              '& .MuiInputLabel-root': { color: '#fff' },
              '& .MuiOutlinedInput-root': {
                color: '#fff',
                '& fieldset': { borderColor: '#fff' },
                '&:hover fieldset': { borderColor: '#fff' },
                '&.Mui-focused fieldset': { borderColor: '#fff' },
              },
            }}
          />

          <Button
            variant="contained"
            type="submit"
            sx={{
              mt: 2,
              backgroundColor: '#fff',
              color: '#000',
              '&:hover': { backgroundColor: '#f0f0f0' },
            }}
          >
            Register
          </Button>
        </form>

        <Button
          sx={{
            mt: 3,
            backgroundColor: '#000000ff',
            color: '#ffffffff',
            '&:hover': { backgroundColor: '#000000ff' },
          }}
          component={RouterLink}
          to="/login"
        >
          Already have an account? Login
        </Button>
      </Container>
    </div>
  );
}
